/**
 * Login-Page.
 */

import { useState, type FormEvent } from "react";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Label } from "../components/Label";
import { useAuth, ApiCallError } from "../lib/auth";
import { t } from "../locales/de";

export function LoginPage() {
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    // Client-side-Validation statt HTML-`required` — ist verlässlicher in
    // allen Browsern und blockt das Form-Submit nicht still.
    if (!email.trim() || !password) {
      setError(t.login.errorGeneric);
      return;
    }
    setSubmitting(true);
    try {
      await login(email, password);
      window.location.href = "/";
    } catch (err) {
      console.error("Login-Fehler:", err);
      if (err instanceof ApiCallError) {
        if (err.status === 429) {
          setError(t.login.errorRateLimit);
        } else if (err.status === 401) {
          setError(t.login.errorInvalid);
        } else {
          setError(err.apiError.message || t.login.errorGeneric);
        }
      } else {
        setError(t.login.errorGeneric);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t.appName}</h1>
          <p className="text-sm text-muted-foreground">{t.appTagline}</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg border bg-card p-6 shadow-sm"
        >
          <div className="space-y-2">
            <Label htmlFor="email">{t.login.email}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t.login.password}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? t.login.submitting : t.login.submit}
          </Button>
        </form>

        <p className="text-center text-xs text-muted-foreground">
          {t.appName} · v0.1.0
        </p>
      </div>
    </div>
  );
}
